import React, { useEffect, useMemo } from 'react'
import Icon from '../../../ui/Icon/Icon'
import PhotoManager from '../../PhotoManager/PhotoManager'
import Field from '../../../ui/Field/Field'
import AmeCampaignProductMultiSelect from '../../Marketing/AmeCampaigns/AmeCampaignProductMultiSelect'
import useLazyFirestoreCatalog from '../../Marketing/UtmBuilder/useLazyFirestoreCatalog'
import buildRelatedProductSearchFilters from '../../../api/marketing/buildRelatedProductSearchFilters'
import { enrichBlogProductRefs } from '../../../api/marketing/buildRelatedProductSearchIndex'
import { loadRelatedProductCatalogCached } from '../../../api/blog/loadRelatedProductCatalogCached'
import BlogTextBlock from './BlogTextBlock'
import BlogProductSetBlock from '../article/BlogProductSetBlock'
import { BlogArticleImage } from '../article/BlogArticleBlock'
import usePairedImageAspect from '../article/usePairedImageAspect'
import {
  BLOG_GALLERY_LAYOUT,
  BLOG_GALLERY_LAYOUT_HINTS,
  BLOG_GALLERY_LAYOUT_OPTIONS,
  normalizeBlogGalleryLayout,
} from '../../../api/blog/blogConstants'
import styles from './BlogEditor.module.scss'

const photosFromRef = (photo) => {
  if (!photo?.imageUrl) return []
  return [
    {
      imageUrl: photo.imageUrl,
      storagePath: photo.storagePath || null,
      fileName: photo.fileName || 'photo',
      needUpload: false,
      isAvatar: true,
    },
  ]
}

const refFromPhotos = (photos) => {
  if (!Array.isArray(photos) || !photos.length) return null
  const photo = photos[0]
  const imageUrl = photo?.imageUrl || photo?.publicUrl || null
  if (!imageUrl) return null
  return {
    imageUrl,
    storagePath: photo.storagePath || null,
    fileName: photo.fileName || null,
  }
}

const photosFromGalleryItems = (items) => {
  if (!Array.isArray(items)) return []
  return items
    .map((item, index) => {
      const photo = item?.photo
      const imageUrl = photo?.imageUrl || photo?.publicUrl || null
      if (!imageUrl) return null
      return {
        imageUrl,
        storagePath: photo.storagePath || null,
        fileName: photo.fileName || `gallery-${index + 1}`,
        needUpload: Boolean(photo.needUpload),
        file: photo.file || null,
        isAvatar: index === 0,
        caption: item.caption || '',
        alt: item.alt || '',
      }
    })
    .filter(Boolean)
}

const galleryItemsFromPhotos = (photos) => {
  if (!Array.isArray(photos)) return []
  return photos
    .map((photo) => {
      const imageUrl = photo?.imageUrl || photo?.publicUrl || null
      if (!imageUrl) return null
      return {
        photo: {
          imageUrl,
          storagePath: photo.storagePath || null,
          fileName: photo.fileName || null,
          needUpload: Boolean(photo.needUpload),
        },
        caption: typeof photo.caption === 'string' ? photo.caption : '',
        alt: typeof photo.alt === 'string' ? photo.alt : '',
      }
    })
    .filter(Boolean)
}

const parseYoutubeId = (url) => {
  if (typeof url !== 'string') return ''
  const match = url
    .trim()
    .match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{6,})/)
  return match?.[1] || ''
}

function PhotoField({ materialId, label, photo, onChange }) {
  return (
    <div className={styles.photoField}>
      <p className={styles.fieldLabel}>{label}</p>
      {materialId ? (
        <PhotoManager
          projectId={materialId}
          projectPath={`blog/${materialId}`}
          formData={photosFromRef(photo)}
          persistDeletes={false}
          compact
          syncState={(photos) => onChange(refFromPhotos(photos))}
        />
      ) : (
        <p className={styles.hint}>Збережіть матеріал локально (автоматично), щоб завантажити зображення в Storage.</p>
      )}
    </div>
  )
}

function ImageLinkFields({ block, onChange }) {
  return (
    <>
      <Field
        fieldType="input"
        config={{ label: 'Посилання', type: 'text' }}
        value={block.href || ''}
        changed={(event) => onChange({ ...block, href: event.target.value })}
      />
      <Field
        fieldType="checkbox"
        config={{ label: 'Відкривати в новій вкладці' }}
        value={block.openInNewTab === true}
        changed={(event) => onChange({ ...block, openInNewTab: event.target.checked })}
      />
    </>
  )
}

function ProductSetFields({ block, onChange }) {
  const {
    data: searchIndex,
    isLoading,
    loadError,
    load,
  } = useLazyFirestoreCatalog({
    loadCatalog: loadRelatedProductCatalogCached,
    errorMessage: 'Не вдалося завантажити товари',
    logLabel: '[Blog productSet]',
  })
  const filters = useMemo(() => buildRelatedProductSearchFilters([]), [])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!Array.isArray(searchIndex) || !searchIndex.length) return
    const current = Array.isArray(block.productRefs) ? block.productRefs : []
    if (!current.length) return
    const enriched = enrichBlogProductRefs(current, searchIndex)
    if (enriched === current) return
    onChange({ ...block, productRefs: enriched })
    // Only re-enrich when catalog arrives / refs identity changes from outside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchIndex, block.productRefs])

  return (
    <div className={styles.customBody}>
      <Field
        fieldType="input"
        config={{ label: 'Назва добірки', type: 'text' }}
        value={block.title || ''}
        changed={(event) => onChange({ ...block, title: event.target.value })}
      />
      <AmeCampaignProductMultiSelect
        value={Array.isArray(block.productRefs) ? block.productRefs : []}
        onChange={(productRefs) => onChange({ ...block, productRefs })}
        searchIndex={searchIndex || []}
        filters={filters}
        isLoading={isLoading}
        loadError={loadError}
        onLoadRequest={load}
        canBrowseOnFocus
        reorderable
        buttonPicker
        label="Додати товар"
      />
    </div>
  )
}

/** Equal-width cells; shared height = smaller of the two natural heights (at that width). */
function BeforeAfterPreview({ block }) {
  const beforeUrl = block.before?.photo?.imageUrl || null
  const afterUrl = block.after?.photo?.imageUrl || null
  const aspectRatio = usePairedImageAspect(beforeUrl, afterUrl)
  const imgStyle = aspectRatio ? { aspectRatio } : undefined

  return (
    <div className={styles.beforeAfterPreview}>
      <div>
        <p className={styles.fieldLabel}>До</p>
        {beforeUrl ? (
          <img src={beforeUrl} alt={block.before?.alt || 'До'} style={imgStyle} />
        ) : (
          <p className={styles.hint}>—</p>
        )}
      </div>
      <div>
        <p className={styles.fieldLabel}>Після</p>
        {afterUrl ? (
          <img src={afterUrl} alt={block.after?.alt || 'Після'} style={imgStyle} />
        ) : (
          <p className={styles.hint}>—</p>
        )}
      </div>
    </div>
  )
}

function CompactPreview({ block, onChange, onFocusEditor }) {
  if (block.type === 'image') {
    return block.photo?.imageUrl ? (
      <figure className={styles.mediaPreview}>
        <BlogArticleImage block={block} />
        {block.caption ? <figcaption>{block.caption}</figcaption> : null}
      </figure>
    ) : (
      <p className={styles.blockEmptyHint}>Зображення не вибрано</p>
    )
  }
  if (block.type === 'gallery') {
    const items = Array.isArray(block.items) ? block.items.filter((item) => item?.photo?.imageUrl) : []
    const layout = normalizeBlogGalleryLayout(block.layout)
    if (!items.length) {
      return <p className={styles.blockEmptyHint}>Галерея порожня</p>
    }
    if (layout === BLOG_GALLERY_LAYOUT.carousel) {
      const first = items[0]
      return (
        <div className={styles.galleryCarouselPreview}>
          <img src={first.photo.imageUrl} alt={first.alt || ''} />
          {items.length > 1 ? (
            <p className={styles.galleryLayoutBadge}>Карусель · {items.length} фото</p>
          ) : null}
        </div>
      )
    }
    if (layout === BLOG_GALLERY_LAYOUT.collage) {
      const count = Math.min(items.length, 6)
      return (
        <div className={`${styles.galleryCollagePreview} ${styles[`galleryCollage_${count}`] || ''}`.trim()}>
          {items.slice(0, 6).map((item, index) => (
            <img key={`${item.photo.imageUrl}-${index}`} src={item.photo.imageUrl} alt={item.alt || ''} />
          ))}
          {items.length > 6 ? (
            <p className={styles.galleryLayoutBadge}>+{items.length - 6} у колажі на вітрині як сітка</p>
          ) : null}
        </div>
      )
    }
    return (
      <div className={styles.galleryPreview}>
        {items.map((item, index) => (
          <img key={`${item.photo.imageUrl}-${index}`} src={item.photo.imageUrl} alt={item.alt || ''} />
        ))}
      </div>
    )
  }
  if (block.type === 'beforeAfter') {
    return <BeforeAfterPreview block={block} />
  }
  if (block.type === 'youtube') {
    return block.videoId ? (
      <div className={styles.youtubePreview}>YouTube: {block.videoId}</div>
    ) : (
      <p className={styles.blockEmptyHint}>Вкажіть посилання YouTube у налаштуваннях блоку</p>
    )
  }
  if (block.type === 'videoUpload') {
    return block.video?.url ? (
      <p className={styles.hint}>Відео: {block.video.url}</p>
    ) : (
      <p className={styles.blockEmptyHint}>Вкажіть URL відео у налаштуваннях</p>
    )
  }
  if (block.type === 'productSet') {
    const refs = Array.isArray(block.productRefs) ? block.productRefs : []
    if (!refs.length && !block.title) {
      return <p className={styles.blockEmptyHint}>Товари не додано</p>
    }
    return <BlogProductSetBlock block={block} />
  }
  if (block.type === 'ameButton') {
    return (
      <div className={styles.buttonPreviewWrap}>
        <div className={styles.buttonPreview}>
          <Icon name="long-arrow-right" weight="light" />
          <span>{block.label || 'Кнопка'}</span>
        </div>
      </div>
    )
  }
  if (block.type === 'imageText') {
    return (
      <div className={`${styles.imageTextPreview} ${block.imagePosition === 'right' ? styles.imageRight : ''}`.trim()}>
        {block.photo?.imageUrl ? (
          <BlogArticleImage block={block} />
        ) : (
          <div className={styles.blockEmptyHint}>Немає фото</div>
        )}
        <div className={styles.imageTextCopy}>
          <BlogTextBlock
            block={{ ...block, type: 'paragraph', content: block.content }}
            onChange={(next) => onChange?.({ ...block, content: next.content })}
            onFocusEditor={onFocusEditor}
          />
        </div>
      </div>
    )
  }
  return null
}

function BlogCustomBlock({ block, materialId, onChange, onFocusEditor, compact = false }) {
  if (compact) {
    return <CompactPreview block={block} onChange={onChange} onFocusEditor={onFocusEditor} />
  }

  if (block.type === 'image') {
    return (
      <div className={styles.customBody}>
        <PhotoField materialId={materialId} label="Зображення" photo={block.photo} onChange={(photo) => onChange({ ...block, photo })} />
        <Field fieldType="input" config={{ label: 'Підпис', type: 'text' }} value={block.caption || ''} changed={(event) => onChange({ ...block, caption: event.target.value })} />
        <Field fieldType="input" config={{ label: 'Alt', type: 'text' }} value={block.alt || ''} changed={(event) => onChange({ ...block, alt: event.target.value })} />
        <ImageLinkFields block={block} onChange={onChange} />
      </div>
    )
  }

  if (block.type === 'gallery') {
    const layout = normalizeBlogGalleryLayout(block.layout)
    return (
      <div className={styles.customBody}>
        <Field
          fieldType="select"
          config={{
            label: 'Макет',
            getOptions: BLOG_GALLERY_LAYOUT_OPTIONS,
          }}
          value={layout}
          changed={(event) =>
            onChange({ ...block, layout: normalizeBlogGalleryLayout(event.target.value) })
          }
        />
        <p className={styles.hint}>{BLOG_GALLERY_LAYOUT_HINTS[layout]}</p>
        <div className={styles.photoField}>
          <p className={styles.fieldLabel}>Фото галереї</p>
          {materialId ? (
            <PhotoManager
              projectId={`${materialId}-gallery`}
              projectPath={`blog/${materialId}/gallery`}
              formData={photosFromGalleryItems(block.items)}
              persistDeletes={false}
              compact
              syncState={(photos) => onChange({ ...block, items: galleryItemsFromPhotos(photos) })}
            />
          ) : (
            <p className={styles.hint}>
              Збережіть матеріал локально (автоматично), щоб завантажити зображення в Storage.
            </p>
          )}
        </div>
      </div>
    )
  }

  if (block.type === 'beforeAfter') {
    return (
      <div className={styles.customBody}>
        <div className={styles.twoCol}>
          <PhotoField
            materialId={materialId}
            label="До"
            photo={block.before?.photo || null}
            onChange={(photo) =>
              onChange({
                ...block,
                before: { ...(block.before || {}), photo, alt: block.before?.alt || 'До' },
              })
            }
          />
          <PhotoField
            materialId={materialId}
            label="Після"
            photo={block.after?.photo || null}
            onChange={(photo) =>
              onChange({
                ...block,
                after: { ...(block.after || {}), photo, alt: block.after?.alt || 'Після' },
              })
            }
          />
        </div>
      </div>
    )
  }

  if (block.type === 'imageText') {
    return (
      <div className={styles.customBody}>
        <Field
          fieldType="select"
          config={{
            label: 'Позиція зображення',
            getOptions: [
              { value: 'left', label: 'Зліва' },
              { value: 'right', label: 'Справа' },
            ],
          }}
          value={block.imagePosition || 'left'}
          changed={(event) => onChange({ ...block, imagePosition: event.target.value })}
        />
        <PhotoField materialId={materialId} label="Зображення" photo={block.photo} onChange={(photo) => onChange({ ...block, photo })} />
        <p className={styles.hint}>Текст редагується прямо в блоці на полотні.</p>
        <ImageLinkFields block={block} onChange={onChange} />
      </div>
    )
  }

  if (block.type === 'youtube') {
    return (
      <div className={styles.customBody}>
        <Field
          fieldType="input"
          config={{ label: 'YouTube URL', type: 'text' }}
          value={block.url || ''}
          changed={(event) => {
            const url = event.target.value
            onChange({ ...block, url, videoId: parseYoutubeId(url) })
          }}
        />
      </div>
    )
  }

  if (block.type === 'videoUpload') {
    return (
      <div className={styles.customBody}>
        <Field
          fieldType="input"
          config={{ label: 'URL відео (mp4/webm)', type: 'text' }}
          value={block.video?.url || ''}
          changed={(event) =>
            onChange({
              ...block,
              video: event.target.value.trim()
                ? { url: event.target.value.trim(), storagePath: null, fileName: null }
                : null,
            })
          }
        />
        <Field
          fieldType="input"
          config={{ label: 'Підпис', type: 'text' }}
          value={block.caption || ''}
          changed={(event) => onChange({ ...block, caption: event.target.value })}
        />
      </div>
    )
  }

  if (block.type === 'productSet') {
    return <ProductSetFields block={block} onChange={onChange} />
  }

  if (block.type === 'ameButton') {
    return (
      <div className={styles.customBody}>
        <Field fieldType="input" config={{ label: 'Текст кнопки', type: 'text' }} value={block.label || ''} changed={(event) => onChange({ ...block, label: event.target.value })} />
        <Field fieldType="input" config={{ label: 'Посилання', type: 'text' }} value={block.href || ''} changed={(event) => onChange({ ...block, href: event.target.value })} />
        <Field
          fieldType="checkbox"
          config={{ label: 'Відкривати в новій вкладці' }}
          value={block.openInNewTab !== false}
          changed={(event) => onChange({ ...block, openInNewTab: event.target.checked })}
        />
      </div>
    )
  }

  return <p className={styles.hint}>Невідомий тип блоку</p>
}

export default BlogCustomBlock
